import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {ColorInputComponent} from '../../../shared/forms/color-input/color-input.component';
import {SliderInputComponent} from '../../../shared/forms/slider-input/slider-input.component';
import {SelectInputComponent} from '../../../shared/forms/select-input/select-input.component';
import {OverlayElement} from '../../../api/interfaces/overlay-element.interface';
import {DefaultValues} from '@maporium-workspace/shared';
import {StyleRowComponent} from '../../../shared/style-groups/style-row.component';

@Component({
  styles: [`
    /* MAP-1533: left-align the leading style icons so they start at the same
       horizontal position as the STYLES section header (like PROPERTIES and
       COMMENTS above), instead of centered in their column. Scoped here only. */
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex {
      justify-content: flex-start !important;
      /* MAP-1533 round 2: a bootstrap col-2 (16.7% of the row) is far wider
         than the icon glyph needs; even left-aligned, the leftover width
         inside this column was a large dead gap before the field starts,
         narrowing the field itself. Give the icon a fixed footprint and let
         the field that follows reclaim the rest. */
      flex: 0 0 28px !important;
      max-width: 28px !important;
      width: 28px !important;
    }
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex + [class*="col-"] {
      flex: 1 1 0% !important;
      max-width: none !important;
      width: auto !important;
    }
  `],
  selector: 'maporium-overlay-element-style-light',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ColorInputComponent,
    SliderInputComponent,
    SelectInputComponent,
    StyleRowComponent,
  ],
  template: `
    <ng-container [formGroup]="formGroup">
      <fieldset formGroupName="properties">
        <!-- MAP-990: overlay elements have no dependent styles today, so every
             row is its own core style with no expand/collapse toggle (AC4) —
             maporium-style-row still reserves the column so widths line up
             with node/link/smap style rows, and keeps a divider between rows
             like regular properties (AC7). -->
        <maporium-style-row [divider]="false">
          <app-color-input formControlName="backgroundColor"
                           [value]="element?.properties?.backgroundColor || DefaultValues.overlayElement.properties.backgroundColor"
                           [icon]="'palette'"
                           [placeholder]="'Background Color'"></app-color-input>
        </maporium-style-row>

        <maporium-style-row>
          <app-color-input formControlName="borderColor"
                           [value]="element?.properties?.borderColor || DefaultValues.overlayElement.properties.borderColor"
                           [icon]="'border_color'"
                           [placeholder]="'Border Color'"></app-color-input>
        </maporium-style-row>

        <maporium-style-row>
          <app-slider-input formControlName="borderWidth"
                            [label]="'line_weight'"
                            [isIcon]="true"
                            [value]="toNumber(element?.properties?.borderWidth !== undefined ? element!.properties!.borderWidth : DefaultValues.overlayElement.properties.borderWidth)"
                            [placeholder]="'Border Width'"
                            [max]="16"
                            [min]="0"
                            [step]="1"></app-slider-input>
        </maporium-style-row>

        <maporium-style-row>
          <app-select-input formControlName="borderStyle"
                            [label]="'line_style'"
                            [isIcon]="true"
                            [placeholder]="'Border Style'"
                            [options]="borderStyles"></app-select-input>
        </maporium-style-row>

        <maporium-style-row>
          <app-slider-input formControlName="opacity"
                            [label]="'opacity'"
                            [isIcon]="true"
                            [value]="toNumber(element?.properties?.opacity !== undefined ? element!.properties!.opacity : DefaultValues.overlayElement.properties.opacity)"
                            [placeholder]="'Opacity'"
                            [max]="1"
                            [min]="0"
                            [step]="0.05"
                            [showTickMarks]="false"></app-slider-input>
        </maporium-style-row>
      </fieldset>
    </ng-container>
  `,
})
export class OverlayElementStyleLightComponent {
  protected readonly DefaultValues = DefaultValues;

  @Input() formGroup: any;
  @Input() element: OverlayElement | undefined;

  borderStyles = [
    {key: 'solid', value: 'Solid', icon: 'horizontal_rule'},
    {key: 'dashed', value: 'Dashed', icon: 'line_style'},
    {key: 'dotted', value: 'Dotted', icon: 'more_horiz'},
  ];

  toNumber(value: any): number {
    return value !== undefined && value !== null ? Number(value) : 0;
  }
}
